import Dexie, { type Table } from 'dexie'
import { generateUUID } from '@/lib/action-policy'
import { mutationRequestConfig } from '@/lib/mutations'
import { http } from '@/services/http'
import type { CachedDelivery, MutationPayloadValue, PackageSummary, PendingMedia, PendingMutation, PodUploadItem, UserRole } from '@/types/logistics'

class ThreeAsassDatabase extends Dexie {
  routePackages!: Table<PackageSummary, string>
  cachedDeliveries!: Table<CachedDelivery, string>
  pendingMutations!: Table<PendingMutation, string>
  pendingMedia!: Table<PendingMedia, string>
  podChecklist!: Table<PodUploadItem, string>

  constructor() {
    super('3asass-offline')
    this.version(1).stores({
      routePackages: 'id,status,zone',
      pendingPods: 'id,packageId,status,createdAt',
      podChecklist: 'id,status'
    })
    this.version(2).stores({
      routePackages: 'id,state,zone,syncStatus',
      pendingMutations: 'id,idempotencyKey,role,status,createdAt',
      podChecklist: 'id,status'
    })
    this.version(3).stores({
      routePackages: 'id,state,zone,syncStatus',
      cachedDeliveries: 'id,role,state,lastUpdated',
      pendingMutations: 'id,idempotencyKey,role,status,createdAt',
      pendingMedia: 'id,mutationId,field,createdAt',
      podChecklist: 'id,status'
    })
  }
}

export const offlineDb = new ThreeAsassDatabase()

export async function queueMutation(record: PendingMutation) {
  const { mutation, media } = extractPendingMedia(record)

  await offlineDb.transaction('rw', offlineDb.pendingMutations, offlineDb.pendingMedia, async () => {
    await offlineDb.pendingMutations.put(mutation)

    if (media.length > 0) {
      await offlineDb.pendingMedia.bulkPut(media)
    }
  })
}

export async function listPendingMutations() {
  return offlineDb.pendingMutations.orderBy('createdAt').toArray()
}

export async function cacheDeliveries(role: Extract<UserRole, 'agent' | 'courier'>, packages: PackageSummary[]) {
  const now = new Date().toISOString()

  await offlineDb.cachedDeliveries.bulkPut(
    packages
      .filter((item) => Boolean(item.deliveryId))
      .map((item) => ({
        id: item.deliveryId as string,
        role,
        state: item.state,
        packageSummary: item,
        lastUpdated: now
      }))
  )
}

export async function listCachedDeliveries(role: Extract<UserRole, 'agent' | 'courier'>) {
  const deliveries = await offlineDb.cachedDeliveries.where('role').equals(role).reverse().sortBy('lastUpdated')

  return deliveries.map((delivery) => ({
    ...delivery.packageSummary,
    syncStatus: delivery.packageSummary.syncStatus === 'synced' ? 'pending' : delivery.packageSummary.syncStatus
  }))
}

export async function replayPendingMutations(options: { retryFailed?: boolean } = {}) {
  const pending = (await listPendingMutations()).filter((mutation) => {
    if (mutation.status === 'pending' || mutation.status === 'syncing') {
      return true
    }

    // 'failed' means the server answered and refused, so the automatic replay leaves it alone
    // rather than repeating the same refusal for ever. The manual "sync now" passes retryFailed,
    // because otherwise the button next to a failed row did nothing at all for that row.
    return Boolean(options.retryFailed) && mutation.status === 'failed'
  })

  for (const mutation of pending) {
    try {
      await offlineDb.pendingMutations.update(mutation.id, { status: 'syncing' })
      const hydratedMutation = await hydratePendingMedia(mutation)

      await http.request(mutationRequestConfig(hydratedMutation))

      await offlineDb.transaction('rw', offlineDb.pendingMutations, offlineDb.pendingMedia, async () => {
        await offlineDb.pendingMutations.delete(mutation.id)
        await offlineDb.pendingMedia.where('mutationId').equals(mutation.id).delete()
      })
    } catch (error) {
      const status = responseStatus(error)
      const code = responseCode(error)

      if (status === 409) {
        await offlineDb.pendingMutations.update(mutation.id, {
          status: code === 'IDEMPOTENCY_IN_PROGRESS' ? 'pending' : 'conflict',
          lastErrorCode: code ?? 'HTTP_409',
          lastErrorAt: new Date().toISOString()
        })

        if (code === 'IDEMPOTENCY_IN_PROGRESS') {
          break
        }

        continue
      }

      await offlineDb.pendingMutations.update(mutation.id, {
        // A 4xx means the server understood the request and refused it: retrying would repeat
        // the same refusal for ever, so it waits for a person to look at it. Anything else —
        // no response at all (a dead zone that still reports online), a timeout, a 5xx — is
        // worth trying again, so it stays queued instead of stranding the proof on the phone.
        status: !navigator.onLine || !isServerRefusal(status) ? 'pending' : 'failed',
        lastErrorCode: code ?? (status ? `HTTP_${status}` : 'NETWORK_UNKNOWN'),
        lastErrorAt: new Date().toISOString()
      })

      if (!navigator.onLine) {
        break
      }
    }
  }
}

function extractPendingMedia(record: PendingMutation): { mutation: PendingMutation; media: PendingMedia[] } {
  const payload = { ...record.payload }
  const media: PendingMedia[] = []

  /**
   * A field carries either one file or a list of them (`photos: File[]`). Both have to leave the
   * payload and come back from the media table: a File left inside the record cannot be stored
   * by IndexedDB, so the whole mutation is lost before it is ever queued, and the operator is
   * told the server refused an action the server never saw.
   */
  const extract = (field: string, value: MutationPayloadValue): MutationPayloadValue => {
    if (value instanceof Blob) {
      const id = generateUUID()
      const file = value instanceof File ? value : null

      media.push({
        id,
        mutationId: record.id,
        field,
        fileName: file?.name ?? `${field}.jpg`,
        mimeType: value.type || 'application/octet-stream',
        blob: value,
        createdAt: record.createdAt
      })

      return `__pending_media__:${id}`
    }

    if (Array.isArray(value)) {
      return value.map((item) => extract(field, item))
    }

    return value
  }

  Object.entries(payload).forEach(([field, value]) => {
    payload[field] = extract(field, value)
  })

  return {
    mutation: { ...record, payload },
    media
  }
}

async function hydratePendingMedia(record: PendingMutation): Promise<PendingMutation> {
  const media = await offlineDb.pendingMedia.where('mutationId').equals(record.id).toArray()

  if (media.length === 0) {
    return record
  }

  const mediaByMarker = new Map(media.map((item) => [`__pending_media__:${item.id}`, item]))
  const payload = { ...record.payload }

  /**
   * The mirror of the extraction above: markers come back as Files, list fields included, so a
   * queued `photos: File[]` replays with every photo the operator attached.
   */
  const hydrate = (field: string, value: MutationPayloadValue): MutationPayloadValue => {
    if (typeof value === 'string' && value.startsWith('__pending_media__:')) {
      const item = mediaByMarker.get(value)

      if (!item) {
        throw new Error(`Pending media missing for field ${field}.`)
      }

      return new File([item.blob], item.fileName, { type: item.mimeType })
    }

    if (Array.isArray(value)) {
      return value.map((entry) => hydrate(field, entry))
    }

    return value
  }

  Object.entries(payload).forEach(([field, value]) => {
    payload[field] = hydrate(field, value)
  })

  return { ...record, payload }
}

function responseStatus(error: unknown): number | undefined {
  const errorLike = error as { status?: number; response?: { status?: number } } | null

  return errorLike?.response?.status ?? errorLike?.status
}

function responseCode(error: unknown): string | undefined {
  const errorLike = error as { code?: string; response?: { data?: { code?: string; error?: { code?: string } } } } | null

  return errorLike?.response?.data?.code ?? errorLike?.response?.data?.error?.code ?? errorLike?.code
}

/**
 * Did the server receive the request and refuse it (4xx), or did it never get an answer?
 *
 * The difference decides whether a queued field action deserves another attempt: a refusal is
 * final until a person looks at it, while a missing answer (a dead zone, a timeout, a crashed
 * server) is exactly what the queue exists for.
 */
function isServerRefusal(status: number | undefined): boolean {
  return typeof status === 'number' && status >= 400 && status < 500
}
